import { useMutation } from '@apollo/client'
import { Box } from '@mui/material'
import ToggleEditMultiSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditMultiSelectField'
import ToggleEditSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditSelectField'
import isEqual from 'lodash.isequal'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import {
    UpdateCandidateIsDeferredMba,
    UpdateCandidatePrograms,
} from 'recruiter/graphql/candidateProfile.gql'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'
import { EDUCATION_SECTIONS } from '.'
import EditButtonsContainer from './EditButtonsContainer'
import EducationSectionTitleContainer from './EducationSectionTitleContainer'

interface MiscellaneousProps {
    edit: EDUCATION_SECTIONS | null
    setEdit: Dispatch<SetStateAction<EDUCATION_SECTIONS | null>>
    programs: DropdownOption[]
    programOptions: DropdownOption[]
    candidateId: string
    closeAfterSave: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
    setEditWithUnsavedChanges: (newEdit: EDUCATION_SECTIONS | null) => void
    isDeferredMba: boolean | undefined
}

const Miscellaneous: FC<MiscellaneousProps> = ({
    edit,
    setEdit,
    programs,
    programOptions,
    candidateId,
    closeAfterSave,
    setUnsavedChangesExist,
    setEditWithUnsavedChanges,
    isDeferredMba,
}) => {
    const toast = useToast()
    const [selectedPrograms, setSelectedPrograms] =
        useState<DropdownOption[]>(programs)
    useEffect(() => {
        if (edit !== EDUCATION_SECTIONS.PROGRAMS) {
            setSelectedPrograms(programs)
        }
    }, [edit, setSelectedPrograms, programs])
    const [deferredMba, setDeferredMba] = useState<boolean | undefined>(
        isDeferredMba
    )

    const unsavedChangesExist =
        !isEqual(programs, selectedPrograms) || isDeferredMba !== deferredMba
    const [submitPrograms] = useMutation(UpdateCandidatePrograms)

    const [submitDeferredMba] = useMutation(UpdateCandidateIsDeferredMba)

    const noneValue = programOptions.find((p) => p.value === 'None')
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            await submitPrograms({
                variables: {
                    candidateId,
                    programIds:
                        selectedPrograms.length === 0
                            ? [noneValue?.id || '']
                            : selectedPrograms.map((program) => program.id),
                },
            })
            await submitDeferredMba({
                variables: {
                    candidateId,
                    isDeferredMba: deferredMba ?? false,
                },
            })
            toast({
                severity: 'success',
                title: 'Success',
                message: 'Miscellaneous education values have been updated',
            })
            closeAfterSave()
        } catch (e) {
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update miscellaneous education values',
            })
        }
    }
    const filteredProgramOptions = programOptions.filter(
        (program) => program.value !== 'None'
    )

    useEffect(() => {
        if (unsavedChangesExist && edit === EDUCATION_SECTIONS.PROGRAMS) {
            setUnsavedChangesExist(true)
        }
    }, [unsavedChangesExist, edit, setUnsavedChangesExist])

    return (
        <>
            <form onSubmit={handleSubmit}>
                <EducationSectionTitleContainer
                    setEditWithUnsavedChanges={setEditWithUnsavedChanges}
                    edit={edit}
                    sectionType={EDUCATION_SECTIONS.PROGRAMS}
                    title={'Miscellaneous'}
                    titleId="programsLabel"
                />
                <Box>
                    <ToggleEditMultiSelectField
                        dropdownData={filteredProgramOptions}
                        edit={edit === EDUCATION_SECTIONS.PROGRAMS}
                        label="Programs"
                        labelId="programsLabel"
                        limit={100}
                        onInputChange={(newValue) =>
                            setSelectedPrograms(newValue)
                        }
                        selectedDropdownData={selectedPrograms}
                    />
                </Box>
                <Box
                    marginTop={
                        edit !== EDUCATION_SECTIONS.PROGRAMS
                            ? getPxAsRem(16)
                            : 0
                    }
                >
                    <ToggleEditSelectField
                        dropdownData={[
                            { id: 'true', value: 'Yes' },
                            { id: 'false', value: 'No' },
                        ]}
                        edit={edit === EDUCATION_SECTIONS.PROGRAMS}
                        label="Deferred MBA"
                        labelId="deferredMbaLabel"
                        onChange={(e) =>
                            setDeferredMba(
                                e.target.value === 'true' ? true : false
                            )
                        }
                        selectedDropdownData={
                            deferredMba
                                ? { id: 'true', value: 'Yes' }
                                : { id: 'false', value: 'No' }
                        }
                    />
                </Box>

                <EditButtonsContainer
                    activeEditingSection={edit}
                    sectionType={EDUCATION_SECTIONS.PROGRAMS}
                    setEdit={setEdit}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                    unsavedChangesExist={unsavedChangesExist}
                    onCancel={() => {
                        setSelectedPrograms(programs)
                    }}
                />
            </form>
        </>
    )
}

export default Miscellaneous
